import {createEntityAdapter, createSlice, isAnyOf, SerializedError} from "@reduxjs/toolkit";
import {RootState} from "../../../redux/store";
import {FeAvailability} from "./types";
import {
    createAvailabilityThunk, deleteAvailabilityThunk,
    getAvailabilityThunk,
    listAvailabilitiesThunk, listWorkerAvailabilities,
    updateAvailabilityThunk
} from "./availabilities.thunk";

const availabilityAdapter = createEntityAdapter<FeAvailability>();

type ErrorMap = {
    [id: string]: SerializedError | undefined;
};

const availabilitiesSlice = createSlice({
    name: 'availabilities',
    initialState: availabilityAdapter.getInitialState({errors: {} as ErrorMap}),
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(deleteAvailabilityThunk.fulfilled, (state, action) => {
                availabilityAdapter.removeOne(state, action.payload);
            })
            .addCase(createAvailabilityThunk.fulfilled, (state, action) => {
                availabilityAdapter.upsertOne(state, action.payload);
                state.errors[action.payload.id] = undefined;
            })
            .addCase(createAvailabilityThunk.rejected, (state, {error}) => {
                state.errors.create = error;
            })
            .addCase(updateAvailabilityThunk.fulfilled, (state, action) => {
                const {id, ...changes} = action.payload;
                availabilityAdapter.updateOne(state, {id, changes});
                state.errors[action.payload.id] = undefined;
            })
            .addCase(updateAvailabilityThunk.rejected, (state, action) => {
                state.errors[action.meta.arg.id] = action.error;
            })
            .addCase(getAvailabilityThunk.fulfilled, (state, action) => {
                availabilityAdapter.upsertOne(state, action.payload);
                state.errors[action.payload.id] = undefined;
            })
            .addCase(getAvailabilityThunk.rejected, (state, action) => {
                state.errors[action.meta.arg.id] = action.error;
            })
            .addMatcher(isAnyOf(listAvailabilitiesThunk.fulfilled, listWorkerAvailabilities.fulfilled), (state, action) => {
                state.errors.list = undefined;
                availabilityAdapter.upsertMany(state, action.payload);
            })
            .addMatcher(isAnyOf(listAvailabilitiesThunk.rejected, listWorkerAvailabilities.rejected), (state, action) => {
                state.errors.list = action.error;
            })
})

export const {
    selectById: selectAvailabilityById,
    selectIds: selectAvailabilityIds,
    selectEntities: selectAvailabilityEntities,
    selectAll: selectAllAvailabilities,
    selectTotal: selectTotalAvailabilities,
} = availabilityAdapter.getSelectors((state: RootState) => state.availabilities);

export default availabilitiesSlice.reducer;
